import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { campaignsApi } from '../../api';

const EditCampaignDialog = ({ open, campaign, onOpenChange, onSaved }) => {
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (campaign) {
      setEditingCampaign({
        id: campaign.id,
        name: campaign.name || '',
        script_type: campaign.script_type || 'sales',
        status: campaign.status || 'paused',
        call_window_start: campaign.call_window_start || '09:00',
        call_window_end: campaign.call_window_end || '17:00',
        max_calls_per_day: campaign.max_calls_per_day || 100
      });
    } else {
      setEditingCampaign(null);
    }
  }, [campaign]);

  const handleSaveEdit = async () => {
    if (!editingCampaign) return;

    setSaving(true);
    try {
      await campaignsApi.update(editingCampaign.id, {
        name: editingCampaign.name,
        script_type: editingCampaign.script_type,
        status: editingCampaign.status,
        call_window_start: editingCampaign.call_window_start,
        call_window_end: editingCampaign.call_window_end,
        max_calls_per_day: editingCampaign.max_calls_per_day
      });

      toast.success(`Campaign "${editingCampaign.name}" updated successfully`);
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (err) {
      console.error('Error updating campaign:', err);
      toast.error(`Failed to update campaign: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-campaign-dialog">
        <DialogHeader>
          <DialogTitle>Edit Campaign</DialogTitle>
        </DialogHeader>
        {editingCampaign && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Campaign Name</Label>
              <Input
                id="campaign-name"
                value={editingCampaign.name}
                onChange={(e) => setEditingCampaign({...editingCampaign, name: e.target.value})}
                placeholder="Enter campaign name"
                data-testid="edit-campaign-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="script-type">Script Type</Label>
              <Select
                value={editingCampaign.script_type}
                onValueChange={(value) => setEditingCampaign({...editingCampaign, script_type: value})}
              >
                <SelectTrigger data-testid="edit-campaign-script-type">
                  <SelectValue placeholder="Select script type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="service">Service Reminder</SelectItem>
                  <SelectItem value="financing">Financing Pre-Qual</SelectItem>
                  <SelectItem value="trade_in">Trade-In Outreach</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={editingCampaign.status}
                onValueChange={(value) => setEditingCampaign({...editingCampaign, status: value})}
              >
                <SelectTrigger data-testid="edit-campaign-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="call-window-start">Call Window Start</Label>
                <Input
                  id="call-window-start"
                  type="time"
                  value={editingCampaign.call_window_start}
                  onChange={(e) => setEditingCampaign({...editingCampaign, call_window_start: e.target.value})}
                  data-testid="edit-campaign-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="call-window-end">Call Window End</Label>
                <Input
                  id="call-window-end"
                  type="time"
                  value={editingCampaign.call_window_end}
                  onChange={(e) => setEditingCampaign({...editingCampaign, call_window_end: e.target.value})}
                  data-testid="edit-campaign-end-time"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max-calls">Max Calls Per Day</Label>
              <Input
                id="max-calls"
                type="number"
                min="1"
                max="1000"
                value={editingCampaign.max_calls_per_day}
                onChange={(e) => setEditingCampaign({...editingCampaign, max_calls_per_day: parseInt(e.target.value) || 100})}
                data-testid="edit-campaign-max-calls"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-campaign-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditCampaignDialog;
